import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Mic, Circle, Upload, FileText, ChevronDown, ChevronUp, ChevronLeft } from 'lucide-react';
import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk';
import { toast } from 'react-hot-toast';
import ScheduleBanner from './ScheduleBanner';
import { uploadToS3 } from '../utils/s3';
import AudioPlayer from './AudioPlayer';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import Loader from './Loader';
import ConfirmDialog from './ConfirmDialog';
import ExcalidrawCanvas from './ExcalidrawCanvas';

const RecordingStatus = ({ status, lectureId }) => {
  const { theme } = useTheme();

  let message = '';
  let icon = null;
  let color = '';

  switch (status) {
    case 'recording':
      message = 'Recording in progress...';
      color = '#ef4444'; // red
      break;
    case 'saving':
      message = 'Saving lecture...';
      color = theme.primary;
      break;
    case 'saved':
      message = 'Lecture saved successfully! Click "Analyze" to process it.';
      color = '#22c55e'; // green
      break;
    case 'ready':
      message = 'Ready to record';
      color = theme.text;
      break;
    default:
      return null;
  }

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
      style={{
        backgroundColor: `${color}10`,
        color: color,
        border: `1px solid ${color}30`
      }}
    >
      {status === 'recording' && (
        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
      )}
      {message}
    </div>
  );
};

const compressAudioBlob = async (blob) => {
  // Convert to 32kbps MP3 using Web Audio API
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const arrayBuffer = await blob.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  const offlineContext = new OfflineAudioContext({
    numberOfChannels: 1,
    length: audioBuffer.length,
    sampleRate: 22050, // Lower sample rate for smaller file size
  });

  const source = offlineContext.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(offlineContext.destination);
  source.start();

  const renderedBuffer = await offlineContext.startRendering();
  const wavBlob = await new Promise(resolve => {
    const length = renderedBuffer.length;
    const wav = new Float32Array(length);
    renderedBuffer.copyFromChannel(wav, 0);

    const encoder = new MediaRecorder(new MediaStream(), {
      mimeType: 'audio/webm;codecs=opus',
      bitsPerSecond: 32000 // 32kbps
    });

    encoder.ondataavailable = (e) => resolve(e.data);
    encoder.start();
    encoder.stop();
  });

  return new Blob([wavBlob], { type: 'audio/webm' });
};

// Add this new component for consistent waiting states
const AnalysisCard = ({ title, content, children }) => (
  <div className="bg-theme rounded-lg p-4 border border-theme">
    <h3 className="text-lg font-medium text-theme mb-3">
      {title}
    </h3>
    {content ? children : null}
  </div>
);

const ClassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();

  const [classData, setClassData] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lectureId, setLectureId] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingError, setRecordingError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const socketRef = useRef(null);
  const deepgramRef = useRef(null);
  const transcriptRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const [selectedContext, setSelectedContext] = useState(null);
  const [visibleContexts, setVisibleContexts] = useState({});
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [isConfirmStopOpen, setIsConfirmStopOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingStatus, setRecordingStatus] = useState('ready');
  const [lastAnalyzedText, setLastAnalyzedText] = useState('');
  const [autoAnalyzeTimeout, setAutoAnalyzeTimeout] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [nextAnalysisWordCount, setNextAnalysisWordCount] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const WORDS_BETWEEN_ANALYSIS = 200; // Increased threshold for less frequent analysis
  const [lastAnalysisTime, setLastAnalysisTime] = useState(0);
  const [isFirstAnalysis, setIsFirstAnalysis] = useState(true);
  const FIRST_ANALYSIS_MINUTES = 3;
  const SUBSEQUENT_ANALYSIS_MINUTES = 15;
  const analysisTimeoutRef = useRef(null);
  const [audioContext, setAudioContext] = useState(null);
  const [activeTab, setActiveTab] = useState('transcript');
  const [isHandwritingMode, setIsHandwritingMode] = useState(false);
  const [mobileView, setMobileView] = useState('transcript'); // New state for mobile tab view
  const [isReanalyzing, setIsReanalyzing] = useState(false);

  // Clear any errors and timeouts when component unmounts
  useEffect(() => {
    return () => {
      setRecordingError(null);
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchClassData = async () => {
      if (!isLoaded || !isSignedIn || !id) return;

      try {
        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes/${id}`
        );
        const data = await response.json();

        if (isMounted) {
          setClassData(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching class:', error);
          toast.error('Failed to load class data');
        }
      }
    };

    fetchClassData();

    return () => {
      isMounted = false;
    };
  }, [id, isLoaded, isSignedIn]);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript]);

  // Initialize Deepgram client when component mounts
  useEffect(() => {
    try {
      const apiKey = process.env.REACT_APP_DEEPGRAM_API_KEY;
      if (apiKey) {
        // Initialize with v3 SDK format
        deepgramRef.current = createClient(apiKey);
        console.log('Deepgram client initialized with v3 SDK');
      }
    } catch (error) {
      console.error('Error initializing Deepgram client:', error);
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.finish();
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  const saveLecture = async (analysisData = null, transcriptToSave = null) => {
    if (isSaving) return;

    setIsSaving(true);
    setRecordingStatus('saving');
    try {
      // Use the provided transcript if available, otherwise use the state
      const transcriptContent = transcriptToSave || transcript;
      
      console.log('Saving lecture with data:', {
        classId: id,
        title: `Lecture on ${new Date().toLocaleDateString()}`,
        date: new Date().toISOString(),
        transcript: transcriptContent,
        transcriptLength: transcriptContent ? transcriptContent.length : 0,
        audioUrl: audioUrl,
        status: 'unprocessed'
      });
      
      const lectureData = {
        classId: id,
        title: `Lecture on ${new Date().toLocaleDateString()}`,
        date: new Date().toISOString(),
        transcript: transcriptContent,
        analysis: analysisData,
        audioUrl: audioUrl,
        status: 'unprocessed'
      };

      const response = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-lecture',
        {
          method: 'POST',
          body: JSON.stringify(lectureData),
        }
      );

      const data = await response.json();
      console.log('Save lecture response:', data);
      
      if (data.lecture?.id) {
        setLectureId(data.lecture.id);
        setRecordingStatus('saved');
        setLastAnalyzedText('');
      }

      toast.success('Lecture saved successfully');
      return data.lecture;
    } catch (error) {
      console.error('Error saving lecture:', error);
      toast.error('Failed to save lecture');
      setRecordingStatus('error');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const configureAudioSession = async () => {
    try {
      // Create audio context with specific options for iOS
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const context = new AudioContext({
        sampleRate: 44100,
        latencyHint: 'interactive'
      });

      // Enable background audio mode if available (iOS)
      if (navigator.mediaSession) {
        navigator.mediaSession.setActionHandler('play', () => { });
        navigator.mediaSession.setActionHandler('pause', () => { });
        // Set metadata to keep audio session active
        navigator.mediaSession.metadata = new MediaMetadata({
          title: 'Recording Lecture',
          artist: 'Classify',
          album: 'Lecture Recording'
        });
      }

      // Request wake lock to prevent device sleep
      try {
        if ('wakeLock' in navigator) {
          await navigator.wakeLock.request('screen');
        }
      } catch (err) {
        console.warn('Wake Lock not supported:', err);
      }

      setAudioContext(context);
      return context;
    } catch (error) {
      console.error('Error configuring audio session:', error);
      throw error;
    }
  };

  const startRecording = async () => {
    console.log('Starting recording...');
    try {
      // Configure audio session before requesting media
      await configureAudioSession();

      // iOS-specific constraints for audio recording
      const constraints = {
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          sampleRate: { ideal: 44100 },
          channelCount: { ideal: 1 }
        }
      };

      // Request media with specific constraints for iOS
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Add audio focus request for iOS
      if (audioContext?.state === 'suspended') {
        await audioContext.resume();
      }

      // Check if MediaRecorder is supported with webm
      let options = { mimeType: 'audio/webm' };
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        // Fallback for iOS
        options = { mimeType: 'audio/mp4' };
        if (!MediaRecorder.isTypeSupported('audio/mp4')) {
          // Last resort fallback
          options = {};
        }
      }

      mediaRecorderRef.current = new MediaRecorder(stream, {
        ...options,
        bitsPerSecond: 32000,
        audioBitsPerSecond: 32000
      });

      const audioChunks = [];

      mediaRecorderRef.current.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);

          // Send chunk to Deepgram
          try {
            console.log('Sending audio chunk to Deepgram');
            socketRef.current?.send(event.data);
          } catch (error) {
            console.error('Error sending chunk to Deepgram:', error);
          }
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        try {
          // Compress audio before upload
          const compressedBlob = await compressAudioBlob(audioBlob);
          const uploadedUrl = await uploadAudio(compressedBlob);
          console.log('Uploaded audio URL:', uploadedUrl);

          if (uploadedUrl) {
            setAudioUrl(uploadedUrl);
            setAudioFile(compressedBlob);
          } else {
            throw new Error('Failed to upload audio');
          }
        } catch (error) {
          console.error('Error uploading audio:', error);
          toast.error('Failed to upload audio. Please try again.');
          setRecordingStatus('error');
        }
      };

      // Create Deepgram connection with error handling
      try {
        const connection = deepgramRef.current.listen.live({
          language: 'en-US',
          smart_format: true,
          paragraphs: true,
          model: 'nova-3',
          interim_results: false,
        });

        connection.addListener(LiveTranscriptionEvents.Open, () => {
          console.log('Deepgram connection opened successfully');
          try {
            mediaRecorderRef.current.start(1000);
            setIsRecording(true);

            // Start recording duration timer
            setRecordingDuration(0);
            durationIntervalRef.current = setInterval(() => {
              setRecordingDuration(prev => prev + 1);
            }, 1000);
          } catch (error) {
            console.error('Error starting MediaRecorder:', error);
            throw new Error('Failed to start recording');
          }
        });

        connection.addListener(LiveTranscriptionEvents.Close, () => {
          console.log('Deepgram connection closed');
          if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
          }
        });

        connection.addListener(LiveTranscriptionEvents.Transcript, (data) => {
          if (data?.channel?.alternatives?.[0]?.transcript) {
            const receivedTranscript = data.channel.alternatives[0].transcript;
            setTranscript(prevTranscript => {
              if (!prevTranscript.includes(receivedTranscript)) {
                const newTranscript = (prevTranscript + ' ' + receivedTranscript).trim();

                // Clear any existing timeout
                if (autoAnalyzeTimeout) {
                  clearTimeout(autoAnalyzeTimeout);
                }

                // Set a new timeout to check for analysis
                const timeout = setTimeout(() => {
                  checkForAnalysis(newTranscript);
                }, 5000); // Wait 5 seconds after last transcript update

                setAutoAnalyzeTimeout(timeout);
                return newTranscript;
              }
              return prevTranscript;
            });
          }
        });

        connection.addListener(LiveTranscriptionEvents.Error, (error) => {
          console.error('Deepgram error:', error);
          setRecordingError('Transcription error occurred');
          toast.error('Transcription error occurred');
        });

        socketRef.current = connection;
      } catch (error) {
        console.error('Error creating Deepgram connection:', error);
        throw new Error('Failed to initialize transcription service');
      }

      setRecordingStatus('recording');
      setIsFirstAnalysis(true);
      setLastAnalysisTime(0);

    } catch (error) {
      console.error('Error starting recording:', error);
      setRecordingError(error.message || 'Failed to start recording');
      toast.error(error.message || 'Failed to start recording');
      setRecordingStatus('ready');

      // Cleanup any partial setup
      if (mediaRecorderRef.current?.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      if (socketRef.current) {
        socketRef.current.finish();
      }
    }
  };

  const stopRecording = async () => {
    console.log('Stopping recording...');
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        if (socketRef.current) {
          socketRef.current.finish();
        }
        setIsRecording(false);
        setRecordingStatus('ready');

        // Clear recording duration timer
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
        }

        // Stop all tracks on the stream
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());

        // Automatically save the lecture after stopping
        if (transcript) {
          console.log('Stopping recording, saving lecture with transcript length:', transcript.length);
          const savedLecture = await saveLecture(null, transcript);
          if (savedLecture) {
            // Automatically trigger first analysis
            await analyzeTranscript(transcript);
          }
        }
      } catch (error) {
        console.error('Error stopping recording:', error);
        toast.error('Failed to stop recording properly');
        setRecordingStatus('error');
      }
    }
    setIsConfirmStopOpen(false);
  };

  const analyzeTranscript = async (transcriptToAnalyze = transcript) => {
    if (!transcriptToAnalyze) {
      toast.error('No transcript available');
      return;
    }

    if (isAnalyzing) return;

    setIsAnalyzing(true);
    const isAutoAnalysis = transcriptToAnalyze !== transcript;

    if (!isAutoAnalysis) {
      toast.loading('Analyzing lecture...', { id: 'analysis' });
    }

    try {
      const response = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/analyze-lecture',
        {
          method: 'POST',
          body: JSON.stringify({
            lectureText: transcriptToAnalyze,
            currentDate: new Date().toISOString().split('T')[0]
          })
        }
      );

      const data = await response.json();
      let analysisResult;
      try {
        const cleanedJson = data.analysis.replace(/^```json\s*|\s*```\s*$/g, '').trim();
        analysisResult = JSON.parse(cleanedJson);
      } catch (parseError) {
        console.error('Error parsing analysis JSON:', parseError);
        throw new Error('Invalid analysis response from server');
      }

      setAnalysis(analysisResult);
      setLastAnalyzedText(transcriptToAnalyze);
      setLastAnalysisTime(Date.now());

      if (isFirstAnalysis) {
        setIsFirstAnalysis(false);
      }

      // Only update the lecture if we have a lectureId
      if (lectureId) {
        const updateResponse = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/update-lecture/${id}/${lectureId}`,
          {
            method: 'PUT',
            body: JSON.stringify({
              analysis: analysisResult,
              status: 'processed',
              title: analysisResult.title || `Lecture on ${new Date().toLocaleDateString()}`
            })
          }
        );

        if (!updateResponse.ok) {
          throw new Error('Failed to update lecture with analysis');
        }
      }

      if (!isAutoAnalysis) {
        toast.success('Lecture analyzed successfully', { id: 'analysis' });
      }
    } catch (err) {
      console.error('Error analyzing transcript:', err);
      if (!isAutoAnalysis) {
        toast.error('Failed to analyze the lecture', { id: 'analysis' });
      }
      setAnalysis(null);
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress(0);
    }
  };

  const scheduleNextAnalysis = () => {
    if (analysisTimeoutRef.current) {
      clearTimeout(analysisTimeoutRef.current);
    }

    const interval = isFirstAnalysis ? FIRST_ANALYSIS_MINUTES : SUBSEQUENT_ANALYSIS_MINUTES;
    const timeSinceLastAnalysis = Date.now() - lastAnalysisTime;
    const timeUntilNextAnalysis = Math.max(0, interval * 60 * 1000 - timeSinceLastAnalysis);

    analysisTimeoutRef.current = setTimeout(() => {
      if (isRecording && transcript && !isAnalyzing) {
        analyzeTranscript(transcript);
      }
    }, timeUntilNextAnalysis);
  };

  const checkForAnalysis = (newTranscript) => {
    if (!isRecording || isAnalyzing) return;

    const timeSinceLastAnalysis = Date.now() - lastAnalysisTime;
    const minutesSinceLastAnalysis = timeSinceLastAnalysis / (60 * 1000);
    const interval = isFirstAnalysis ? FIRST_ANALYSIS_MINUTES : SUBSEQUENT_ANALYSIS_MINUTES;

    // Update progress towards next analysis
    const progress = (minutesSinceLastAnalysis / interval) * 100;
    setAnalysisProgress(Math.min(progress, 100));

    // Calculate minutes until next analysis
    const minutesUntilNext = Math.max(0, interval - minutesSinceLastAnalysis);
    setNextAnalysisWordCount(Math.round(minutesUntilNext));

    // If enough time has passed, analyze
    if (minutesSinceLastAnalysis >= interval) {
      analyzeTranscript(newTranscript);
      scheduleNextAnalysis();
    }
  };

  // Add this effect to handle analysis scheduling
  useEffect(() => {
    if (isRecording) {
      scheduleNextAnalysis();
    } else {
      // Clear the timeout when not recording
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
      }
    }

    return () => {
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
      }
    };
  }, [isRecording, lastAnalysisTime]);

  const uploadAudio = async (file) => {
    if (!file) return null;

    try {
      // Create form data for the upload
      const formData = new FormData();
      formData.append('audio', file);

      // Show upload progress
      const uploadWithProgress = async (url, options) => {
        return new Promise(async (resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open(options.method || 'POST', url);
          
          // Add authentication header using Clerk
          const token = await getToken();
          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.response);
                resolve(response);
              } catch (error) {
                reject(new Error('Invalid JSON response from server'));
              }
            } else {
              let errorMessage = `Upload failed with status ${xhr.status}`;
              try {
                const errorJson = JSON.parse(xhr.response);
                if (errorJson.error) {
                  errorMessage = errorJson.error;
                }
              } catch (e) {
                // If we can't parse the error response, use the default message
              }
              reject(new Error(errorMessage));
            }
          };
          
          xhr.ontimeout = () => reject(new Error('Upload timed out'));
          xhr.onerror = () => reject(new Error('Network error during upload'));
          
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              setUploadProgress(progress);
            }
          };
          
          xhr.send(options.body);
        });
      };
      
      // Upload the file
      const response = await uploadWithProgress(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/upload-audio',
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.success) {
        throw new Error('Failed to upload audio file');
      }
      
      return response.audioUrl;
    } catch (err) {
      console.error('Error uploading audio:', err);
      return null;
    } finally {
      setUploadProgress(0);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Check file type
    const acceptableTypes = ['audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/webm', 'audio/ogg', 'audio/m4a'];
    if (!acceptableTypes.includes(file.type) && !file.type.startsWith('audio/')) {
      toast.error('Please upload a valid audio file (WAV, MP3, WebM, OGG, or M4A format)');
      return;
    }
    
    setIsLoading(true);
    toast.loading('Processing audio file...', { id: 'upload' });
    
    // Try the direct transcription approach first
    // This avoids CORS issues by sending the file directly to our server
    try {
      console.log('Attempting direct transcription...');
      const formData = new FormData();
      formData.append('audio', file);
      
      // Add detailed logging
      console.log(`File to upload: ${file.name}, size: ${file.size}, type: ${file.type}`);
      
      const token = await getToken();
      console.log('Authentication token obtained');
      
      const directTranscribeResponse = await fetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/direct-transcribe',
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (!directTranscribeResponse.ok) {
        const errorText = await directTranscribeResponse.text();
        console.error(`Direct transcription failed with status ${directTranscribeResponse.status}: ${errorText}`);
        throw new Error(`Direct transcription failed (${directTranscribeResponse.status}), trying fallback approach`);
      }
      
      const directResult = await directTranscribeResponse.json();
      console.log('Direct transcription response received', directResult);
      
      if (directResult.success && directResult.transcript) {
        console.log('Direct transcription successful!');
        console.log('Transcript received:', directResult.transcript.substring(0, 100) + '...');
        console.log('Transcript length:', directResult.transcript.length);
        setTranscript(directResult.transcript);
        setAudioUrl(directResult.audioUrl);
        setAudioFile(file);
        
        // Save the lecture with the transcript
        toast.loading('Saving lecture...', { id: 'upload' });
        console.log('About to save lecture with transcript length:', directResult.transcript.length);
        const savedLecture = await saveLecture(null, directResult.transcript);
        
        if (savedLecture) {
          toast.success('Audio file processed successfully!', { id: 'upload' });
          
          // Trigger analysis of the transcript
          await analyzeTranscript(directResult.transcript);
        } else {
          console.error('Failed to save lecture, savedLecture is falsy');
        }
        
        setIsLoading(false);
        return; // Success! Stop here
      }
      
      throw new Error('Direct transcription response missing data');
    } catch (directError) {
      console.error('Direct transcription failed:', directError);
      console.log('Falling back to two-step process...');
      // Continue with the two-step process (upload then transcribe)
    }
    
    // Two-step fallback process
    try {
      // First upload the file to our server
      const formData = new FormData();
      formData.append('audio', file);
      
      // Use XHR to track upload progress
      const uploadWithProgress = (url, formData) => {
        return new Promise(async (resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          
          // Add authentication header
          const token = await getToken();
          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.response);
                resolve(response);
              } catch (error) {
                console.error('Error parsing server response:', error);
                reject(new Error('Invalid response from server'));
              }
            } else {
              let errorMessage = `Upload failed with status ${xhr.status}`;
              try {
                const errorJson = JSON.parse(xhr.response);
                if (errorJson.error) {
                  errorMessage = errorJson.error;
                }
                console.error('Upload error response:', errorJson);
              } catch (e) {
                // Use default message if can't parse
                console.error('Upload error (unparseable):', xhr.response);
              }
              reject(new Error(errorMessage));
            }
          };
          
          xhr.onerror = () => {
            console.error('Network error during upload');
            reject(new Error('Network error during upload'));
          }
          xhr.ontimeout = () => {
            console.error('Upload timed out');
            reject(new Error('Upload timed out'));
          }
          
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              setUploadProgress(progress);
              console.log(`Upload progress: ${progress}%`);
            }
          };
          
          xhr.send(formData);
        });
      };
      
      console.log('Uploading audio file to server...');
      const uploadResponse = await uploadWithProgress(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/upload-audio',
        formData
      );
      
      if (!uploadResponse.success || !uploadResponse.audioUrl) {
        console.error('Upload response missing required data:', uploadResponse);
        throw new Error('Failed to upload audio file');
      }
      
      console.log('Audio file uploaded successfully. URL:', uploadResponse.audioUrl);
      setAudioUrl(uploadResponse.audioUrl);
      setAudioFile(file);
      
      // Now request transcription from our server
      toast.loading('Transcribing audio...', { id: 'upload' });
      console.log('Requesting transcription from server...');
      
      const transcribeResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/transcribe',
        {
          method: 'POST',
          body: JSON.stringify({ audioUrl: uploadResponse.audioUrl })
        }
      );
      
      if (!transcribeResponse.ok) {
        const errorData = await transcribeResponse.json();
        console.error('Transcription error response:', errorData);
        throw new Error(errorData.error || 'Transcription failed');
      }

      const transcriptionData = await transcribeResponse.json();
      
      if (!transcriptionData.transcript) {
        throw new Error('No transcript received from server');
      }
      
      console.log('Transcription received:', transcriptionData.transcript.substring(0, 100) + '...');
      console.log('Transcription length:', transcriptionData.transcript.length);
      setTranscript(transcriptionData.transcript);
      
      // Save the lecture with the transcript
      toast.loading('Saving lecture...', { id: 'upload' });
      console.log('About to save lecture with transcript length:', transcriptionData.transcript.length);
      const savedLecture = await saveLecture(null, transcriptionData.transcript);
      
      if (savedLecture) {
        toast.success('Audio file processed successfully!', { id: 'upload' });
        
        // Trigger analysis of the transcript
        await analyzeTranscript(transcriptionData.transcript);
      } else {
        console.error('Failed to save lecture, savedLecture is falsy');
      }
    } catch (err) {
      console.error('Error processing audio:', err);
      toast.error(`Failed to process audio file: ${err.message || 'Unknown error'}`, { id: 'upload' });
      
      // Reset transcript if needed
      if (transcript === 'Transcribing audio file...') {
        setTranscript('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleContext = (section, index) => {
    setVisibleContexts(prev => ({
      ...prev,
      [`${section}-${index}`]: !prev[`${section}-${index}`]
    }));
  };

  const getWaitingMessage = () => {
    if (!transcript) return 'Start recording or upload audio to begin analysis';
    if (isFirstAnalysis) return `First analysis will begin after ${FIRST_ANALYSIS_MINUTES} minutes`;
    return `Next analysis in ${SUBSEQUENT_ANALYSIS_MINUTES} minutes`;
  };

  // Add visibility change handler
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden && isRecording) {
        // Keep audio session active in background
        if (audioContext?.state === 'suspended') {
          await audioContext.resume();
        }

        // Update media session state
        if (navigator.mediaSession) {
          navigator.mediaSession.playbackState = 'playing';
        }
      }
    };

    const handleBeforeUnload = (e) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = 'Recording in progress. Are you sure you want to leave?';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRecording, audioContext]);

  // Add cleanup for audio context
  useEffect(() => {
    return () => {
      if (audioContext) {
        audioContext.close();
      }
    };
  }, [audioContext]);

  // Add this new function
  const checkAndTriggerAnalysis = async () => {
    if (!transcript || isAnalyzing || isReanalyzing) return;

    setIsReanalyzing(true);
    toast.loading('Re-analyzing lecture...', { id: 'reanalysis' });

    try {
      // First check if we already have analysis
      const lectureResponse = await authenticatedFetch(
        `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${id}/${lectureId}`
      );
      
      const lectureData = await lectureResponse.json();
      
      if (!lectureData.analysis) {
        // If no analysis exists, trigger a new one
        await analyzeTranscript(transcript);
        toast.success('Analysis completed successfully', { id: 'reanalysis' });
      } else {
        toast.success('Lecture already has analysis', { id: 'reanalysis' });
      }
    } catch (error) {
      console.error('Error checking/triggering analysis:', error);
      toast.error('Failed to analyze lecture', { id: 'reanalysis' });
    } finally {
      setIsReanalyzing(false);
    }
  };

  // Add this effect to check for analysis when component mounts
  useEffect(() => {
    if (lectureId && transcript) {
      checkAndTriggerAnalysis();
    }
  }, [lectureId]);

  if (!classData) return (
    <div className="h-screen flex items-center justify-center">
      <Loader size="lg" text="Loading class data..." />
    </div>
  );

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-screen flex flex-col bg-theme theme-transition">
      <ScheduleBanner
        onNavigate={(classId) => navigate(`/class/${classId}/lectures`)}
      />

      {/* Header */}
      <header className="px-4 sm:px-6 py-4 border-b border-theme">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-theme truncate">
              {classData?.title || 'Loading...'}
            </h1>
            <p className="text-sm text-accent">
              {new Date().toLocaleDateString()}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {isRecording ? (
              <button
                onClick={() => setIsConfirmStopOpen(true)}
                className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-all"
                disabled={isLoading}
              >
                <Circle size={20} className="animate-pulse" />
                <div className="flex flex-col items-start">
                  <span>Stop Recording</span>
                  <span className="text-xs">{formatDuration(recordingDuration)}</span>
                </div>
              </button>
            ) : (
              <>
                <button
                  onClick={startRecording}
                  className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-red-500 text-white hover:bg-red-600 transition-all"
                  disabled={isLoading || recordingStatus === 'saving'}
                >
                  <Mic size={20} />
                  <span className="whitespace-nowrap">Start Recording</span>
                </button>
                
                <label className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 cursor-pointer transition-all ${
                  isLoading || isRecording 
                    ? 'bg-secondary opacity-60 cursor-not-allowed' 
                    : 'bg-secondary text-accent hover:text-theme'
                }`}>
                  <Upload size={20} />
                  <span className="whitespace-nowrap">Upload Audio</span>
                  <input 
                    type="file"
                    accept="audio/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={isLoading || isRecording}
                  />
                </label>

                {/* Add Reanalyze button */}
                {lectureId && transcript && (
                  <button
                    onClick={checkAndTriggerAnalysis}
                    disabled={isAnalyzing || isReanalyzing}
                    className={`px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-all ${
                      isAnalyzing || isReanalyzing
                        ? 'bg-secondary opacity-60 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary-hover'
                    }`}
                  >
                    <FileText size={20} />
                    <span className="whitespace-nowrap">
                      {isReanalyzing ? 'Analyzing...' : 'Reanalyze'}
                    </span>
                  </button>
                )}
              </>
            )}

            {isHandwritingMode ? (
              <button
                onClick={() => setIsHandwritingMode(false)}
                className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-secondary text-accent hover:text-theme transition-all"
              >
                <ChevronLeft size={20} />
                <span className="whitespace-nowrap">Back to Recording</span>
              </button>
            ) : (
              <button
                onClick={() => setIsHandwritingMode(true)}
                className="px-4 py-2 rounded-lg bg-secondary text-accent hover:text-theme flex items-center justify-center gap-2 transition-all"
              >
                <FileText size={20} />
                <span className="whitespace-nowrap">Handwriting</span>
              </button>
            )}
          </div>
        </div>

        {/* Add status indicator */}
        <div className="mt-4">
          <RecordingStatus status={recordingStatus} lectureId={lectureId} />
        </div>
      </header>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {isHandwritingMode ? (
          <div className="h-full">
            <ExcalidrawCanvas />
          </div>
        ) : (
          <>
            {/* Mobile Tab Navigation */}
            <div className="lg:hidden flex border-b border-theme mt-2">
              <button
                className={`flex-1 py-2 px-4 text-center text-sm font-medium ${
                  mobileView === 'transcript' 
                    ? 'border-b-2 border-primary text-theme' 
                    : 'text-accent'
                }`}
                onClick={() => setMobileView('transcript')}
              >
                Transcript
              </button>
              <button
                className={`flex-1 py-2 px-4 text-center text-sm font-medium ${
                  mobileView === 'analysis' 
                    ? 'border-b-2 border-primary text-theme' 
                    : 'text-accent'
                }`}
                onClick={() => setMobileView('analysis')}
                disabled={!analysis && !audioUrl}
              >
                Analysis {analysis ? <span className="ml-1 inline-block w-2 h-2 rounded-full bg-primary"></span> : ''}
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 p-4 sm:p-6">
              {/* Main Panel - Only show on desktop or when transcript tab is active on mobile */}
              <div className={`lg:col-span-2 flex flex-col max-h-full ${mobileView !== 'transcript' && 'hidden lg:flex'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                  <h2 className="text-lg font-medium text-theme">Live Transcript</h2>
                  <div className="flex items-center gap-4">
                    {/* Analysis Progress Indicator */}
                    {isRecording && analysisProgress > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 bg-secondary rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${analysisProgress}%` }}
                          />
                        </div>
                        <span className="text-xs text-accent">
                          {nextAnalysisWordCount > 0
                            ? `Analysis in ${nextAnalysisWordCount} min${isFirstAnalysis ? ' (first analysis)' : ''}`
                            : 'Analyzing...'}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        analyzeTranscript();
                        if (window.innerWidth < 1024) {
                          setMobileView('analysis');
                        }
                      }}
                      disabled={!transcript || isAnalyzing || !lectureId || isSaving}
                      className={`
                        px-3 py-1.5 rounded-lg flex items-center justify-center gap-2 text-sm transition-all whitespace-nowrap
                        ${transcript && !isAnalyzing && lectureId && !isSaving
                          ? 'bg-primary text-theme hover:bg-primary-hover'
                          : 'bg-secondary text-accent cursor-not-allowed'}
                      `}
                      title={
                        !lectureId
                          ? 'Lecture must be saved first'
                          : !transcript
                            ? 'No transcript available'
                            : isAnalyzing
                              ? 'Analysis in progress...'
                              : isSaving
                                ? 'Saving lecture...'
                                : 'Analyze lecture'
                      }
                    >
                      <FileText size={16} />
                      {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                    </button>
                  </div>
                </div>

                <div className="flex-1 bg-secondary rounded-lg overflow-hidden">
                  <div className="p-4">
                    {recordingError && (
                      <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg">
                        {recordingError}
                      </div>
                    )}
                    <div
                      ref={transcriptRef}
                      className="text-accent whitespace-pre-wrap max-h-[calc(100vh-12rem)] overflow-y-auto"
                    >
                      {transcript || 'No transcript available yet. Start recording or upload an audio file.'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Analysis - Only show on desktop or when analysis tab is active on mobile */}
              <div className={`lg:sticky lg:top-6 space-y-4 lg:space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto mt-4 lg:mt-0 ${mobileView !== 'analysis' && 'hidden lg:block'}`}>
                {/* Audio Player */}
                {audioUrl && (
                  <div className="bg-theme rounded-lg p-4 border border-theme">
                    <h3 className="text-lg font-medium text-theme mb-3">
                      Lecture Audio
                    </h3>
                    <AudioPlayer url={audioUrl} />
                  </div>
                )}

                {/* Analysis Section */}
                <div className="space-y-4 lg:space-y-6 relative">
                  {/* Loading Overlay */}
                  {isAnalyzing && (
                    <div className="absolute inset-0 bg-theme bg-opacity-50 backdrop-blur-sm z-10 flex items-center justify-center overflow-hidden rounded-lg">
                      <div className="flex flex-col items-center gap-3">
                        <Loader size="md" />
                        <p className="text-sm text-accent">Analyzing lecture content...</p>
                      </div>
                    </div>
                  )}

                  {/* Analysis Waiting Message */}
                  {!analysis && !isAnalyzing && (
                    <div className="bg-theme rounded-lg p-4 border border-theme">
                      <p className="text-accent opacity-60 text-center">
                        {getWaitingMessage()}
                      </p>
                    </div>
                  )}

                  {/* Analysis Cards */}
                  <AnalysisCard
                    title="Summary"
                    content={analysis?.summary}
                  >
                    <p className="text-accent">
                      {analysis?.summary || ''}
                    </p>
                  </AnalysisCard>

                  <AnalysisCard
                    title="Key Points"
                    content={analysis?.keyPoints?.length > 0}
                  >
                    <ul className="space-y-2">
                      {analysis?.keyPoints?.map((point, index) => (
                        <ExpandablePoint
                          key={index}
                          point={point}
                          index={index}
                          type="keyPoints"
                          visibleContexts={visibleContexts}
                          toggleContext={toggleContext}
                        />
                      ))}
                    </ul>
                  </AnalysisCard>

                  <AnalysisCard
                    title="Key Dates"
                    content={analysis?.keyDates?.length > 0}
                  >
                    <ul className="space-y-2">
                      {analysis?.keyDates?.map((date, index) => (
                        <ExpandablePoint
                          key={index}
                          point={date}
                          index={index}
                          type="keyDates"
                          visibleContexts={visibleContexts}
                          toggleContext={toggleContext}
                        />
                      ))}
                    </ul>
                  </AnalysisCard>

                  <AnalysisCard
                    title="Announcements"
                    content={analysis?.announcements?.length > 0}
                  >
                    <ul className="space-y-2">
                      {analysis?.announcements?.map((announcement, index) => (
                        <ExpandablePoint
                          key={index}
                          point={{ point: announcement.announcement, context: announcement.context }}
                          index={index}
                          type="announcements"
                          visibleContexts={visibleContexts}
                          toggleContext={toggleContext}
                        />
                      ))}
                    </ul>
                  </AnalysisCard>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={isConfirmStopOpen}
        onClose={() => setIsConfirmStopOpen(false)}
        onConfirm={stopRecording}
        title="Stop Recording"
        message="Are you sure you want to stop recording? This will save the current lecture."
        confirmText="Stop Recording"
        cancelText="Continue Recording"
        isDangerous={true}
      />

      {(isLoading || isSaving) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Loader
            size="lg"
            progress={uploadProgress || undefined}
            text={uploadProgress ? 'Uploading audio...' : (isLoading ? 'Processing...' : 'Saving...')}
          />
        </div>
      )}
    </div>
  );
};

// Refined ExpandablePoint component
const ExpandablePoint = ({ point, index, type, visibleContexts, toggleContext }) => (
  <li className="rounded-lg border border-theme overflow-hidden">
    <button
      onClick={() => toggleContext(type, index)}
      className="w-full flex items-center justify-between p-3 hover:bg-secondary transition-colors"
    >
      <span className="text-theme text-sm">
        {type === 'keyDates' ? `${point.date}: ${point.description}` : point.point}
      </span>
      {visibleContexts[`${type}-${index}`] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
    </button>

    {visibleContexts[`${type}-${index}`] && (
      <div className="px-3 py-2 bg-secondary border-t border-theme">
        <p className="text-sm text-accent">
          {point.context}
        </p>
      </div>
    )}
  </li>
);

export default ClassDetail;